/**
 * i18n Utilities - Client-side translation helpers
 * 
 * Provides hooks and utilities for accessing translations
 * consistently across the application.
 */

'use client';

import { useEffect, useState } from 'react';
import { en, da } from './translations-static';

export type Locale = 'en' | 'da';

// Translation type - use a more flexible type to handle translation differences
type TranslationRecord = Record<string, any>;

/**
 * Get the current locale from storage
 */
export function getLocale(): Locale {
  if (typeof window === 'undefined') return 'da';
  
  // Priority: localStorage > cookie > default
  const stored = localStorage.getItem('locale');
  if (stored === 'en' || stored === 'da') return stored;
  
  const cookie = getCookie('locale');
  if (cookie === 'en' || cookie === 'da') return cookie;
  
  return 'da';
}

/**
 * Get a cookie value by name
 */
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

/**
 * Get translations for the current locale
 */
export function getTranslations(locale?: Locale): TranslationRecord {
  const currentLocale = locale || getLocale();
  return currentLocale === 'da' ? da : en;
}

/**
 * React hook to access translations with automatic locale updates
 */
export function useTranslations() {
  // Lazily read the real stored locale on first render (client only) so the
  // very first render already matches localStorage/cookie. Avoids a post-mount
  // setState that flips the locale and, via useClerkLocalization's key on
  // ClerkProvider, remounts the whole app subtree shortly after page load.
  const [locale, setLocale] = useState<Locale>(() => getLocale());
  const [translations, setTranslations] = useState<TranslationRecord>(() => getTranslations(getLocale()));

  useEffect(() => {
    document.documentElement.lang = locale === 'da' ? 'da' : 'en';

    // Listen for locale changes
    const handleLocaleChange = (event: CustomEvent<Locale>) => {
      setLocale(event.detail);
      setTranslations(event.detail === 'da' ? da : en);
      document.documentElement.lang = event.detail === 'da' ? 'da' : 'en';
    };

    window.addEventListener('localeChange', handleLocaleChange as EventListener);

    return () => {
      window.removeEventListener('localeChange', handleLocaleChange as EventListener);
    };
  }, []);

  return { t: translations, locale };
}

/**
 * Helper to get nested translation value using dot notation
 * Example: getNestedTranslation(translations, 'nav.dashboard')
 */
export function getNestedTranslation(obj: any, path: string, defaultValue?: string): string {
  const keys = path.split('.');
  let current = obj;
  
  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return defaultValue || path;
    }
  }
  
  return typeof current === 'string' ? current : defaultValue || path;
}

/**
 * Translation function with nested key support
 * Usage: t('nav.dashboard') or t('loading')
 */
export function createTranslationFunction(translations: TranslationRecord) {
  return (key: string, fallback?: string): string => {
    return getNestedTranslation(translations, key, fallback);
  };
}
